/**
 * assign-bar.tsx — Thanh người phụ trách ở đầu khung chat: chưa ai nhận → nút "Nhận"; đang phụ trách → "Giao lại".
 * Nút nào hiện do máy chủ quyết định (`assignAccess` trong chi tiết hội thoại); luật thật ở backend assign-rules.ts.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Hand, Loader2, UserRoundCog } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useClaimConversation, type ConversationDetail } from '@/hooks/use-conversations'
import { api, apiError } from '@/lib/api-client'
import { i18n } from '@/lib/i18n'
import { useAuthStore } from '@/stores/auth-store'
import { cn } from '@/lib/utils'
import { AssignDialog } from './assign-dialog'
import { assignBarState } from './assign-lib'

/**
 * `row` (điện thoại): thanh nằm thành MỘT HÀNG riêng dưới đầu khung chat — đầu khung đã chật (quay lại, ảnh, tên, tìm kiếm, chế độ AI, ⓘ, ⋮),
 * nhét thêm nhãn + "Nhận" + "Giao" vào đó thì đè lên tên. Nhãn trạng thái bên trái (cắt bằng …), nút bên phải.
 */
/** Nhãn trạng thái phụ trách ngắn gọn ("Của tôi" / "Chưa phân công" / "Phụ trách: …"). */
export function assignLabel(state: NonNullable<ReturnType<typeof assignBarState>>): string {
  if (state.kind === 'unassigned') return i18n.t('convList:assign.unassigned')
  if (state.kind === 'mine') return i18n.t('convList:assign.mine')
  return i18n.t('convList:assign.assignedTo', { name: state.name })
}

/**
 * Logic nhận / giao dùng chung cho thanh (`AssignBar`) và mục trong menu ⋮ trên điện thoại. `dialog` phải được vẽ NGOÀI menu
 * (menu đóng lại là mục bị gỡ, hộp thoại đi theo) — chat-panel đặt nó cạnh khung chat.
 */
export function useAssignActions(convId: string, conv: ConversationDetail | undefined) {
  const myId = useAuthStore((s) => s.user?.id)
  const claim = useClaimConversation(convId)
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const state = assignBarState(conv, myId)

  // Giao cho người khác xong, mình có thể không còn quyền xem hội thoại này (chỉ mức Tiếp nhận) → rời khỏi nó.
  const onAssigned = async () => {
    try {
      await api.get(`/conversations/${convId}`)
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status === 403) navigate('/conversations', { replace: true })
    }
  }

  const onClaim = async () => {
    try {
      await claim.mutateAsync()
      toast.success(i18n.t('convList:assign.claimed'))
    } catch (e) {
      toast.error(apiError(e))
    }
  }

  const dialog = state ? (
    <AssignDialog convId={convId} open={open} onClose={() => setOpen(false)} hasAssignee={state.kind !== 'unassigned'} convName={conv?.displayName} onAssigned={onAssigned} />
  ) : null

  return { state, claiming: claim.isPending, onClaim, openAssign: () => setOpen(true), dialog }
}

export function AssignBar({ convId, conv, row = false }: { convId: string; conv: ConversationDetail | undefined; row?: boolean }) {
  const { t } = useTranslation('convList')
  const { state, claiming, onClaim, openAssign, dialog } = useAssignActions(convId, conv)
  if (!state) return null

  return (
    <>
      <div className={row ? 'flex items-center gap-2 border-b bg-muted/30 px-3 py-1.5' : 'flex items-center gap-1.5'} data-testid="assign-bar">
        {/* Điện thoại: nhãn chiếm phần còn lại của hàng (đẩy nút sang phải) và tự cắt bằng … */}
        <span className={row ? 'flex min-w-0 flex-1 items-center' : 'contents'}>
          {state.kind === 'unassigned' && <Badge variant="warning" className="whitespace-nowrap px-1.5 py-0 text-[10px]">{t('assign.unassigned')}</Badge>}
          {state.kind === 'mine' && <Badge variant="secondary" className="whitespace-nowrap px-1.5 py-0 text-[10px]">{t('assign.mine')}</Badge>}
          {state.kind === 'other' && (
            <Badge variant="outline" className={cn('truncate px-1.5 py-0 text-[10px]', row ? 'max-w-full' : 'max-w-[140px]')} title={t('assign.assignedTo', { name: state.name })}>
              {t('assign.assignedTo', { name: state.name })}
            </Badge>
          )}
        </span>
        {state.kind === 'unassigned' && state.canClaim && (
          <Button size="sm" className="h-8 gap-1 px-2.5 text-xs" onClick={onClaim} disabled={claiming}>
            {claiming ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Hand className="h-3.5 w-3.5" />} {t('assign.claim')}
          </Button>
        )}
        {state.canAssign && (
          <Button variant="outline" size="sm" className="h-8 gap-1 px-2.5 text-xs" onClick={openAssign}>
            <UserRoundCog className="h-3.5 w-3.5" /> {state.kind === 'unassigned' ? t('assign.assign') : t('assign.reassign')}
          </Button>
        )}
      </div>
      {dialog}
    </>
  )
}
